import type { ReactNode } from 'react';
import { SiteFooter, type FooterView } from './SiteFooter.js';
import { useT } from '../i18n/index.js';
import { SITE_OWNER } from '../lib/siteOwner.js';
import { useDocumentMeta } from '../lib/documentMeta.js';
import type { ContentView } from '../lib/routes.js';

/**
 * The frame every standing page shares: a heading with a way back, the
 * opening line, the page itself, and the navigation at the foot.
 *
 * It also owns the page's title, description and canonical link. Those are
 * derived rather than written per page — the heading and the opening line are
 * already the two sentences that describe the page, and a second, separate set
 * for search engines would drift away from them within a release or two.
 */
export function ContentPage({
  view,
  title,
  lead,
  onBack,
  onNavigate,
  children,
}: {
  view: ContentView;
  title: string;
  lead: string;
  onBack: () => void;
  onNavigate: (view: FooterView) => void;
  children: ReactNode;
}) {
  const t = useT();
  useDocumentMeta({ title: t('meta.pageTitle', { page: title }), description: lead, view });

  return (
    <div className="screen content">
      <div className="row row--between">
        <h1>{title}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <p className="content__lead">{lead}</p>

      {children}

      <div className="spacer" />
      <SiteFooter current={view} onNavigate={onNavigate} />
    </div>
  );
}

/** A titled block of prose. Every section on every content page is one. */
export function ContentSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="stack content__section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/**
 * The postal address, rendered as a real `<address>` so a browser, a reader
 * mode and a crawler all recognise it for what it is.
 */
export function PostalAddress() {
  return (
    <address className="content__address">
      {SITE_OWNER.name}
      <br />
      {SITE_OWNER.street}
      <br />
      {SITE_OWNER.city}
      <br />
      {SITE_OWNER.country}
    </address>
  );
}

/** The mail box, as a `mailto:` link. Shown on three of the four pages. */
export function ContactEmail() {
  return <a href={`mailto:${SITE_OWNER.email}`}>{SITE_OWNER.email}</a>;
}
